<!-- @format -->
<template>
    <div class="woli-popup">
        <button class="close-btn" @click="closePopup"><s3-close-svg /></button>
        <div class="woli-popup-sections">
            <div class="navigation-section">
                <div class="title-row">
                    <q35-woli-svg />
                    <h1>WOLI GPT</h1>
                </div>
                <div class="chat-history-container">
                    <div class="chat-history">
                        <app-button class="new-chat-btn" @click="startNewChat">
                            New Chat
                            <o6-add-svg :color="'#fff'" />
                        </app-button>

                        <woli-chat-history />
                    </div>

                    <app-button
                        class="btn table-actions-btn delete-btn"
                        :disabled="conversations.length === 0 || isLoadingConversations"
                        @click="showDeleteAllConversationsAlertPopup()"
                    >
                        <span>Delete all conversations</span>
                        <s4-delete-svg />
                    </app-button>
                </div>
            </div>
            <div class="chat-section">
                <div ref="conversationContainer" class="conversation-container">
                    <div class="conversation-inner-wrapper">
                        <woli-conversation />
                        <woli-assistant-limit
                            v-if="limitReachedMessage"
                            :message="limitReachedMessage"
                        />
                    </div>
                </div>
                <div class="user-input-section">
                    <woli-conversation-input ref="conversationInput" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { createWoliPopupStore, WOLI_POPUP_TYPE } from '@/components/popups/woli/woli-popup-store';
import { WoliPopupMixin } from '@/components/popups/woli/woli-popup-mixin';
import AppPopup from '@/components/popups/app-popup.vue';
import { WOLI_CLOSE_EVENT, WoliService } from '@/scripts/services/woli/woli-service';
import EventBus from '@/scripts/event-bus';

const S3CloseSvg = () => import('@/components/svg/grid/s3-close-svg');
const Q35WoliSvg = () => import('@/components/svg/grid/q35-woli-nav-svg');
const AppButton = () => import('@/components/common/app-button');
const O6AddSvg = () => import('@/components/svg/grid/o6-add-svg');
const S4DeleteSvg = () => import('@/components/svg/grid/s4-delete-svg');
const WoliChatHistory = () => import('@/components/woli/woli-chat-history');
const WoliConversation = () => import('@/components/woli/woli-conversation');
const WoliConversationInput = () => import('@/components/woli/woli-conversation-input');
const WoliAssistantLimit = () => import('@/components/woli/woli-assistant-limit');

export default {
    name: 'woli-popup',

    components: {
        'app-button': AppButton,
        's3-close-svg': S3CloseSvg,
        'q35-woli-svg': Q35WoliSvg,
        'o6-add-svg': O6AddSvg,
        's4-delete-svg': S4DeleteSvg,
        'woli-chat-history': WoliChatHistory,
        'woli-conversation': WoliConversation,
        'woli-conversation-input': WoliConversationInput,
        'woli-assistant-limit': WoliAssistantLimit,
    },

    mixins: [WoliPopupMixin],

    provide() {
        return {
            popupStore: this.popupStore,
        };
    },

    props: {
        context: {
            type: Object,
            default: () => ({}),
        },

        popupType: {
            type: String,
            default: WOLI_POPUP_TYPE.DEFAULT,
        },

        defaultInput: {
            type: String,
            default: '',
        },
    },

    watch: {},

    data() {
        return {
            popupStore: createWoliPopupStore(),
        };
    },

    computed: {
        limitReachedMessage() {
            return this.popupStore.state.limitReachedMessage;
        },
    },

    async created() {
        EventBus.$on(WOLI_CLOSE_EVENT, this.closePopup);

        this.$store.commit('woli/setIsPopupOpen', true);

        this.popupStore.commit('setContext', this.context);
        this.popupStore.commit('setPopupType', this.popupType);
        this.popupStore.commit('setUserInput', this.defaultInput);

        await this.popupStore.dispatch('loadConversations');
    },

    mounted() {
        window.addEventListener('popstate', this.closePopup);

        this.popupStore.commit('setConversationContainerElement', this.$refs.conversationContainer);
    },

    beforeDestroy() {
        EventBus.$off(WOLI_CLOSE_EVENT, this.closePopup);

        this.$store.commit('woli/setIsPopupOpen', false);

        window.removeEventListener('popstate', this.closePopup);
    },

    methods: {
        closePopup() {
            this.$emit('close');
        },

        startNewChat() {
            this.popupStore.dispatch('startNewChat');
        },

        showDeleteAllConversationsAlertPopup() {
            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: 'Delete all conversations',
                    content:
                        'This will delete all of your conversations. You cannot undo this action. Are you sure you want to delete all of your conversations?',
                    actions: {
                        confirm: {
                            label: 'Delete',
                            type: 'remove',
                            action: this.confirmDeleteAllConversations,
                        },
                        cancel: {
                            label: 'Cancel',
                        },
                    },
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                },
            );
        },

        async confirmDeleteAllConversations() {
            await this.popupStore.dispatch('deleteAllConversations');
        },
    },
};
</script>

<style>
.woli-popup {
    width: 100%;
    height: 100%;
    background: #382153;
    border-radius: 16px;
    padding: 8px;
}

.woli-popup .close-btn {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;
}

.woli-popup .close-btn svg {
    width: 10px;
}

.woli-popup .close-btn {
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #fff;
    box-shadow: 0px 3px 6px #00000029;
}

.woli-popup .close-btn:hover {
    background-color: #f5d5e7;
    cursor: pointer;
}

.woli-popup .woli-popup-sections {
    display: flex;
    align-items: flex-start;
    width: 100%;
    height: 100%;
}

.woli-popup .navigation-section {
    display: flex;
    flex-direction: column;
    padding: 16px 24px 16px 16px;
    color: #fff;
    max-width: 344px;
    width: 100%;
    height: 100%;
}

.woli-popup .navigation-section .title-row {
    display: flex;
    align-items: center;
}

.woli-popup .navigation-section .title-row h1 {
    margin-bottom: 0px;
    font-size: 20px;
    font-family: 'Nunito';
    font-weight: 500;
    margin-left: 8px;
    padding-top: 4px;
}
.woli-popup .chat-history-container {
    height: calc(100% - 32px);
}

.woli-popup .chat-history-container .chat-history {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    width: 100%;
}

.woli-popup .chat-history-container .new-chat-btn {
    width: 100%;
    margin-top: 24px;
    align-items: center;
    justify-content: space-between;
    font-size: 16px;
    font-family: 'Roboto';
    font-weight: 400;
}

.woli-popup .chat-history-container .delete-btn {
    background: transparent;
    border: #ee206f;
    color: #ee206f;
    border: 1px solid #ee206f;
    width: 100%;
    margin-top: 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 16px;
    font-family: 'Roboto';
    font-weight: 400;
}

.woli-popup .chat-history-container .delete-btn:hover {
    background: #ee206f;
    color: #fff;
}

.woli-popup .chat-history-container .delete-btn svg {
    width: 14px;
}

.woli-popup .chat-history-container .delete-btn svg > path {
    fill: #ee206f;
    transition: fill 250ms ease-in 0s;
}

.woli-popup .chat-history-container .delete-btn:hover svg > path {
    fill: #fff;
}

.woli-popup .chat-history-container {
    align-items: center;
    justify-content: space-between;
    display: flex;
    flex-direction: column;
}

.woli-popup .chat-section {
    background: #f7f7f8;
    border-radius: 16px;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    justify-content: space-between;
    min-width: 0;
    overflow: hidden;
}

.woli-popup .user-input-section {
    background: #fff;
    border-bottom-right-radius: 16px;
    border-bottom-left-radius: 16px;
    width: 100%;
    min-height: 72px;
    box-shadow: 0px -2px 12px #8274e833;
    padding: 24px;
    align-items: center;
    justify-content: space-around;
    display: flex;
}

.woli-popup .conversation-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    flex: 1;
    overflow: auto;

    overflow-x: hidden;
}

.woli-popup .conversation-container::-webkit-scrollbar {
    width: 9px;
    height: 9px;
}

.woli-popup .conversation-container::-webkit-scrollbar-track {
    border-radius: 32px !important;
    box-shadow: none;
}

.woli-popup .conversation-container::-webkit-scrollbar-thumb {
    border-radius: 32px !important;
    background: #c1c1c5;
    box-shadow: none;
}

.woli-popup .conversation-container::-webkit-scrollbar-thumb:hover {
    background: #c1c1c5;
}

.woli-popup .conversation-container .conversation-inner-wrapper {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 20px 48px;
    align-items: center;
    min-width: min-content;
}

.woli-popup .empty-chat {
    align-items: center;
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 87px;
    font-size: 18px;
    font-family: 'Nunito';
}

.woli-popup .empty-chat .chat-bubble {
    padding: 24px 32px 24px 18px;
    background: #fff;
    border: 2px solid #8274e833;
    border-radius: 16px;
    max-width: 380px;
    color: #382153;
}

.woli-popup .empty-chat .chat-bubble .svg-container {
    background: #18aeff;
    border-radius: 50%;
    width: 21px;
    height: 21px;
    display: inline-flex;
    align-items: center;
    position: relative;
    top: 5px;
    padding: 0px 4px;
}

.woli-popup .empty-chat .chat-bubble .svg-container svg {
    width: 12px;
    position: relative;
}

.woli-popup .empty-chat .chat-bubble .svg-container svg .a {
    fill: #fff;
}

.woli-popup .empty-chat .idle-woli-container {
    font-size: 31px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.woli-popup .empty-chat .woli-idle-still-animation {
    width: 110px;
    height: 110px;
    background: transparent;
}

.woli-popup .lf-spinner {
    display: none !important;
}

@media (max-width: 850px) {
    .woli-popup .asset-preview-container {
        grid-template-columns: 98%;
        grid-template-areas:
            'preview-asset'
            'information-section';
    }
}

@media (min-width: 850px) {
    .woli-popup .asset-preview-container {
        grid-template-rows: 50px auto;
        grid-template-columns: minmax(auto, 60%) minmax(auto, 40%);
        grid-template-areas:
            'preview-asset information-section'
            'preview-asset information-section';
    }
}
</style>
